import React from 'react';
import { Box, Flex, Text, HStack, Icon, Image, Skeleton, Link, Badge } from '@chakra-ui/react';
import { FiAlertCircle, FiFileText, FiDownload, FiStar, FiCornerUpLeft } from 'react-icons/fi';
import { formatFileSize } from 'utils/whatsappUtils';
import useMediaObjectUrl, { MEDIA_ERROR_TEXT } from '../hooks/useMediaObjectUrl';
import useSwipeToReply from '../hooks/useSwipeToReply';
import useLongPress from '../hooks/useLongPress';
import { prefs } from '../hooks/useLocalPrefs';
import { registerMedia, unregisterMedia, getMedia } from './mediaRegistry';
import { MotionBox, fadeUp, useMotionPrefs, EASE } from './motion';
import Ticks from './Ticks';
import QuotedMessage from './QuotedMessage';
import MessageMenu from './MessageMenu';
import MessageInfoDialog from './MessageInfoDialog';
import VoiceNotePlayer from './VoiceNotePlayer';
import { clock } from './time';
import { parseTemplate } from './templateText';
import Linkify from './Linkify';
import TemplateParts, { TemplateButtons } from './TemplateParts.jsx';
import SenderAvatar from './SenderAvatar';

/**
 * One message, the way WhatsApp draws it.
 *
 * Outbound on the right in WhatsApp's green, inbound on the left on its paper
 * white, each with a small tail on its first bubble of a run and a tight
 * stack for the rest. Time and delivery ticks sit inside the bubble's bottom
 * corner. A quoted reply sits above the text; a hover chevron opens the
 * message menu; on touch, a swipe to the right replies and a long press opens
 * the same menu.
 *
 * ★ THE PALETTE IS WHATSAPP'S, NOT THE APP'S ACCENT — the `wa.*` semantic
 * tokens carry WhatsApp's light AND dark halves, so this is still a theme
 * token, dark mode comes for free, and `npm run theme:check` still passes.
 *
 * ★ MEMOISED. A thread can hold hundreds of these; the parent hands down
 * stable callbacks and only the props of the bubble that changed re-render.
 */
const MEDIA_TYPES = new Set(['image', 'sticker', 'audio', 'video', 'document']);
const READ_MORE_AT = 800;

/** The media part of a bubble: photo, sticker, voice note, video or document. */
function MediaBody({ conversationId, message, outbound, onOpenMedia }) {
	const cached = getMedia(message.wamid);
	const local = message.localUrl;
	const fetched = useMediaObjectUrl({
		conversationId,
		wamid: message.wamid,
		// ★ NOT `&& !cached`, AND THAT MATTERS — see the revoke bug below.
		// This bubble is the sole owner of its object URL: it fetches once and
		// shares the URL through the registry so the lightbox and the reply
		// thumbnail can reuse it. `!cached` used to disable the fetch as soon as
		// the URL was registered — but `cached` is this bubble's OWN entry, so
		// the moment any later render read it back, `enabled` flipped true→false
		// and `useMediaObjectUrl`'s cleanup REVOKED the URL that was still in the
		// registry and still painted in this bubble. The bubble's <img> had
		// already decoded so it kept showing, but every new <img> on that URL —
		// the full-screen view, the quoted-reply preview — got a dead blob and
		// rendered broken. Keeping the fetch enabled for the bubble's life means
		// the URL is revoked exactly once, on unmount, paired with its
		// unregister. Proven: before this, one inbound message left 6/6 visible
		// photos with revoked URLs.
		enabled: Boolean(message.wamid) && !local,
	});
	const url = local ?? cached?.url ?? fetched.url;
	const status = local || cached ? 'ready' : fetched.status;
	const type = message.type;
	const filename = message.media?.filename || 'file';

	// Share the bytes with the lightbox, the quote thumbnail and the contact
	// panel for as long as this bubble is alive.
	React.useEffect(() => {
		// No `|| cached` guard: `cached` is this bubble's own entry, and skipping
		// on it would return without arming the unregister cleanup, leaking a
		// registry entry past unmount. `registerMedia` is idempotent, so simply
		// (re)registering whenever the URL is ready keeps the entry and its
		// cleanup in lockstep.
		if (status !== 'ready' || !url || !message.wamid) return undefined;
		registerMedia(message.wamid, {
			url,
			type,
			mimeType: message.media?.mimeType,
			filename: message.media?.filename,
			caption: message.media?.caption,
			occurredAt: message.occurredAt,
			conversationId,
		});
		return () => unregisterMedia(message.wamid);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, url, message.wamid]);

	if (status === 'error') {
		return (
			<HStack spacing="2" color="wa.inkMuted" fontSize="xs" py="1">
				<Icon as={FiAlertCircle} />
				<Text>{MEDIA_ERROR_TEXT[fetched.error] ?? 'This file could not be loaded.'}</Text>
			</HStack>
		);
	}

	// ★ A MESSAGE WITH NO WAMID IS NOT LOADING — IT IS UNACCOUNTED FOR.
	// The fetch is disabled without a wamid, so `status` stays 'idle' forever and
	// this used to fall through to the skeleton below: a grey rectangle that
	// never resolved, never errored and never explained itself. It happens when
	// the provider accepted a send without returning an id, which leaves nothing
	// stored for the attachment to be fetched from. Say so.
	if (!message.wamid && !local && !cached) {
		return (
			<HStack spacing="2" color="wa.inkMuted" fontSize="xs" py="1">
				<Icon as={FiAlertCircle} />
				<Text>This attachment was sent but not confirmed. Reload to check.</Text>
			</HStack>
		);
	}

	if (status !== 'ready') {
		return (
			<Skeleton
				h={type === 'audio' ? '40px' : '180px'}
				w={type === 'audio' ? '230px' : '240px'}
				borderRadius="md"
				startColor="wa.chip"
				endColor="wa.header"
			/>
		);
	}

	if (type === 'image' || type === 'sticker') {
		return (
			<Box
				as="button"
				type="button"
				display="block"
				onClick={(e) => {
					e.stopPropagation();
					onOpenMedia?.(message.wamid);
				}}
				aria-label={type === 'sticker' ? 'Sticker' : 'Open photo'}
				borderRadius="6px"
				overflow="hidden"
				_hover={{ opacity: 0.95 }}
				_focus={{ boxShadow: '0 0 0 2px var(--chakra-colors-wa-accent)' }}
				cursor={type === 'sticker' ? 'default' : 'zoom-in'}
			>
				<Image
					src={url}
					alt={message.media?.caption || 'Photo'}
					// ⚠ `100%`, NOT A PIXEL CAP. A hard 300px is wider than the
					// bubble itself on any phone — the bubble caps at 82% of a 375px
					// screen, about 300px MINUS its own padding — so the photo pushed
					// out of its own bubble and the row overflowed sideways. A
					// percentage can never be wider than what contains it; the pixel
					// value survives as a ceiling on a desktop.
					maxW={type === 'sticker' ? 'min(160px, 100%)' : 'min(300px, 100%)'}
					maxH={type === 'sticker' ? '160px' : '340px'}
					objectFit="cover"
					draggable={false}
				/>
			</Box>
		);
	}

	if (type === 'audio') {
		return <VoiceNotePlayer url={url} outbound={outbound} />;
	}

	if (type === 'video') {
		return (
			<Box
				as="video"
				controls
				src={url}
				// See the photo above: a fixed width overflows its own bubble on a
				// phone. A video element also needs an explicit width or Safari
				// gives it the intrinsic size of the file.
				w="100%"
				maxW="min(300px, 100%)"
				maxH="340px"
				borderRadius="6px"
				onClick={(e) => e.stopPropagation()}
			/>
		);
	}

	// Document: a card the way WhatsApp shows one, opening in a new tab.
	return (
		<Link href={url} isExternal _hover={{ textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
			<HStack
				spacing="3"
				px="3"
				py="2.5"
				borderRadius="6px"
				bg="wa.reply"
				maxW="min(300px, 100%)"
				// The 220px floor keeps a short filename from collapsing into a
				// stub on a desktop. On a phone it is wider than the bubble, so the
				// card pushed the row sideways — the floor gives way there instead.
				minW={{ base: '0', md: '220px' }}
				w={{ base: '100%', md: 'auto' }}
			>
				<Icon as={FiFileText} boxSize="7" color="wa.inkMuted" />
				<Box minW="0" flex="1">
					<Text fontSize="sm" color="wa.ink" noOfLines={1}>
						{filename}
					</Text>
					<Text fontSize="0.65rem" color="wa.inkMuted">
						{[message.media?.size ? formatFileSize(message.media.size) : null, message.media?.mimeType]
							.filter(Boolean)
							.join(' · ') || 'Document'}
					</Text>
				</Box>
				<Icon as={FiDownload} color="wa.inkMuted" />
			</HStack>
		</Link>
	);
}

/**
 * @param {object} props
 * @param {object} props.message
 * @param {string} props.conversationId
 * @param {boolean} props.first        first bubble of a run from the same side — gets the tail
 * @param {boolean} props.isNew        arrived after the thread was on screen — animates in
 * @param {object|null} props.quoted   the message this one replies to, if loaded
 * @param {string|null} props.quotedWamid
 * @param {string} props.quoteSender   who wrote the quoted message
 * @param {string|null} props.senderLabel   agent name shown above an outbound run
 * @param {string|null} props.senderAvatar  that agent's profileImage path, if any
 * @param {string} props.senderName    for the message-info dialog
 * @param {boolean} props.starred
 * @param {boolean} props.highlighted  briefly, after jumping to it from a quote
 */
function MessageBubble({
	message,
	conversationId,
	first = true,
	isNew = false,
	quoted = null,
	quotedWamid = null,
	quoteSender = '',
	senderLabel = null,
	senderAvatar = null,
	senderName = '',
	starred = false,
	highlighted = false,
	onReply,
	onJumpTo,
	onOpenMedia,
}) {
	const outbound = message.direction === 'outbound';
	const sticker = message.type === 'sticker';
	const isMedia = MEDIA_TYPES.has(message.type);
	const stored = message.content ?? (isMedia ? message.media?.caption : `[${message.type}]`);
	// ★ A TEMPLATE'S STORED TEXT IS AN AUDIT LINE, NOT A MESSAGE.
	// `[template hello_world (en_US)]` is what the send service records — which
	// template, in which language, with which parameters. Shown verbatim it put
	// brackets and a language tag in the thread where a sentence belongs. The
	// name in words goes in the bubble, the parameters under it, and the raw
	// line stays available through Copy. See templateText.js.
	const template = React.useMemo(() => parseTemplate(stored), [stored]);
	// ★ THE SNAPSHOT, WHEN THERE IS ONE, IS THE REAL MESSAGE.
	// A template sent since the snapshot existed carries its own definition and
	// values, so the bubble shows the header, body, footer and buttons the
	// customer actually received — and goes on showing them after the template
	// is re-approved or deleted at Meta, because none of it is read back from
	// the mirror. Everything sent before falls through to the parsed audit line
	// below, which is all those rows will ever have.
	const snapshot = message.template?.components ? message.template : null;
	const rawText = template ? [template.label, template.params].filter(Boolean).join('\n') : stored;
	const [expanded, setExpanded] = React.useState(false);
	const [menuOpen, setMenuOpen] = React.useState(false);
	const [infoOpen, setInfoOpen] = React.useState(false);
	const { dur } = useMotionPrefs();

	// A pending bubble has no wamid yet; WhatsApp cannot quote what it has
	// not accepted.
	const canReply = Boolean(message.wamid) && Boolean(onReply);
	const reply = React.useCallback(() => onReply?.(message), [onReply, message]);

	const swipe = useSwipeToReply(reply, { enabled: canReply });
	const press = useLongPress(() => setMenuOpen(true));
	const gestures = {
		onPointerDown: (e) => {
			swipe.handlers.onPointerDown(e);
			press.onPointerDown(e);
		},
		onPointerMove: (e) => {
			swipe.handlers.onPointerMove(e);
			press.onPointerMove(e);
		},
		onPointerUp: (e) => {
			swipe.handlers.onPointerUp(e);
			press.onPointerUp(e);
		},
		onPointerCancel: (e) => {
			swipe.handlers.onPointerCancel(e);
			press.onPointerCancel(e);
		},
	};

	const text = rawText && !expanded && rawText.length > READ_MORE_AT ? `${rawText.slice(0, READ_MORE_AT)}…` : rawText;

	const copy = () => {
		// The stored line, not the prettified one — that is what an agent pasting
		// into a ticket actually needs.
		if (stored) navigator.clipboard?.writeText(stored).catch(() => {});
	};

	const save = () => {
		const url = message.localUrl ?? getMedia(message.wamid)?.url;
		if (!url) return;
		const a = document.createElement('a');
		a.href = url;
		a.download = message.media?.filename || `whatsapp-${message.type}-${message.wamid}`;
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	return (
		<MotionBox
			id={message.wamid ? `wa-msg-${message.wamid}` : undefined}
			initial={isNew ? fadeUp.initial : false}
			animate={fadeUp.animate}
			transition={{ duration: dur(180), ease: EASE }}
			px="4"
			mt={first ? '2' : '0.5'}
			sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 64px', touchAction: 'pan-y' }}
			{...gestures}
		>
			<Flex
				justify={outbound ? 'flex-end' : 'flex-start'}
				align="center"
				position="relative"
				transform={swipe.dx ? `translateX(${swipe.dx}px)` : undefined}
				transition={swipe.dx ? 'none' : 'transform 160ms ease'}
			>
				{/* The reply arrow that grows as a swipe progresses. */}
				{swipe.dx > 0 && (
					<Icon
						as={FiCornerUpLeft}
						position="absolute"
						left={`-${Math.min(40, swipe.dx * 0.6)}px`}
						color="wa.inkMuted"
						opacity={Math.min(1, swipe.dx / 48)}
						boxSize="5"
					/>
				)}
				<Box
					role="group"
					position="relative"
					// ★ WIDER ON A PHONE. 65% of a 1200px thread is a comfortable
					// 780px; 65% of a 375px phone is 244px, which is about six words
					// a line. WhatsApp itself gives a phone bubble far more of the
					// screen, and the percentage has to follow the screen for the
					// same reason the cap exists at all.
					maxW={{ base: 'min(82%, 520px)', md: 'min(65%, 520px)' }}
					minW={isMedia && !sticker ? '120px' : '60px'}
					bg={sticker ? 'transparent' : outbound ? 'wa.outgoing' : 'wa.incoming'}
					color="wa.ink"
					borderRadius="7.5px"
					borderTopRightRadius={outbound && first && !sticker ? '0' : '7.5px'}
					borderTopLeftRadius={!outbound && first && !sticker ? '0' : '7.5px'}
					px={sticker ? 0 : '2'}
					pt={sticker ? 0 : '1.5'}
					pb={sticker ? 0 : '1'}
					boxShadow={sticker ? 'none' : '0 1px 0.5px rgba(0,0,0,0.13)'}
					outline={highlighted ? '2px solid var(--chakra-colors-wa-accent)' : 'none'}
					outlineOffset="2px"
					transition="outline-color 300ms ease"
					// The tail: a small triangle in the bubble's own colour, on the first
					// bubble of a run only. Drawn with borders so it needs no image and
					// follows the theme.
					_before={
						first && !sticker
							? {
									content: '""',
									position: 'absolute',
									top: 0,
									[outbound ? 'right' : 'left']: '-8px',
									borderStyle: 'solid',
									borderWidth: outbound ? '0 0 10px 8px' : '0 8px 10px 0',
									borderColor: 'transparent',
									[outbound ? 'borderLeftColor' : 'borderRightColor']: outbound
										? 'var(--chakra-colors-wa-outgoing)'
										: 'var(--chakra-colors-wa-incoming)',
								}
							: undefined
					}
				>
					{!sticker && (
						<MessageMenu
							outbound={outbound}
							canReply={canReply}
							hasText={Boolean(rawText)}
							hasMedia={isMedia && Boolean(message.localUrl ?? getMedia(message.wamid))}
							starred={starred}
							isOpen={menuOpen}
							onOpen={() => setMenuOpen(true)}
							onClose={() => setMenuOpen(false)}
							onReply={reply}
							onCopy={copy}
							onInfo={() => setInfoOpen(true)}
							onStar={() => message.wamid && prefs.toggleStar(message.wamid)}
							onSave={save}
						/>
					)}

					{/* ★ A FACE, NOT JUST A NAME — this is what makes a shared thread
					    readable as a group chat. Drawn once per run of consecutive
					    messages from the same person, so a burst of six replies is
					    one avatar rather than six. The name alone was here before
					    and resolved to the literal word "Agent" for everybody, which
					    is the defect this replaces. */}
					{senderLabel && (
						<HStack spacing="1.5" mb="1" pr="6" align="center">
							<SenderAvatar name={senderLabel} profileImage={senderAvatar} size="2xs" />
							<Text fontSize="xs" fontWeight="semibold" color="wa.replyBorder" noOfLines={1}>
								{senderLabel}
							</Text>
						</HStack>
					)}

					{quotedWamid && (
						<Box mt="0.5" mb="1">
							<QuotedMessage
								message={quoted}
								wamid={quotedWamid}
								senderLabel={quoteSender}
								onClick={quoted ? () => onJumpTo?.(quotedWamid) : undefined}
							/>
						</Box>
					)}

					{isMedia && (
						<Box mt={quotedWamid ? 0 : '0.5'} mx={sticker ? 0 : '-0.5'}>
							<MediaBody conversationId={conversationId} message={message} outbound={outbound} onOpenMedia={onOpenMedia} />
						</Box>
					)}

					{(message.type === 'template' || template) && (
						<Badge
							variant="subtle"
							colorScheme="green"
							fontSize="0.6rem"
							mb="1"
							textTransform="none"
							title={
								snapshot?.language || template?.language
									? `Approved template · ${snapshot?.language ?? template.language}`
									: 'Approved template'
							}
						>
							Template
						</Badge>
					)}

					{snapshot && (
						<Box mb="0.5">
							{/* The buttons are drawn AFTER the time row below, the way
							    WhatsApp stacks them — see TemplateButtons. */}
							<TemplateParts components={snapshot.components} values={snapshot.values} showButtons={false} />
						</Box>
					)}

					{!snapshot && text && (
						<Text
							fontSize="14.2px"
							lineHeight="19px"
							whiteSpace="pre-wrap"
							wordBreak="break-word"
							mt={isMedia ? '1.5' : 0}
							pr={text.length < 12 && !isMedia ? '14' : 0}
						>
							<Linkify text={text} />
							{rawText.length > READ_MORE_AT && !expanded && (
								<Text
									as="button"
									type="button"
									color="info"
									fontSize="sm"
									ml="1"
									onClick={(e) => {
										e.stopPropagation();
										setExpanded(true);
									}}
								>
									Read more
								</Text>
							)}
						</Text>
					)}

					{!sticker && (
						<HStack spacing="0" justify="flex-end" mt="0.5" lineHeight="1" align="center">
							{starred && <Icon as={FiStar} boxSize="10px" color="wa.inkMuted" mr="1" aria-label="Starred" />}
							<Text fontSize="11px" color="wa.inkMuted" whiteSpace="nowrap">
								{clock(message.occurredAt)}
							</Text>
							{outbound && <Ticks status={message.status} />}
						</HStack>
					)}

					{/* Last, so the time and ticks sit above the button divider rather
					    than under the final button. */}
					{snapshot && <TemplateButtons components={snapshot.components} values={snapshot.values} />}
					{sticker && (
						<HStack spacing="0" justify={outbound ? 'flex-end' : 'flex-start'} mt="0.5" px="1">
							<Text fontSize="11px" color="wa.inkMuted" bg="wa.datePill" px="1.5" borderRadius="md">
								{clock(message.occurredAt)}
							</Text>
							{outbound && <Ticks status={message.status} />}
						</HStack>
					)}
				</Box>
			</Flex>

			{outbound && (
				<MessageInfoDialog isOpen={infoOpen} onClose={() => setInfoOpen(false)} message={message} senderName={senderName} />
			)}
		</MotionBox>
	);
}

export default React.memo(MessageBubble);
